import { useEffect, useState } from 'react';

import AuthService from '../../shared/services/AuthService';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import User from '../../membership/models/User';
import config from '../../shared/config';

/** Enum to set values by email or username. */
enum eSetBy {
  EMAIL,
  USERNAME
}

/** In-memory cache of data per userkey. */
const userCache = new Map<number, eSetBy>();

/**
 * Hook that determines the icon and iddentifying string to show for a user,
 * e.g. in the dashboard, based on email / username and external schemes.
 * @param userkey Identifier of user by which to load external auth schemes.
 * @param username Username to show, if needed.
 * @param email Email to show, if needed.
 * @param parentkey Identifier of parent account for linked account.
 */
const useExternalUser = (
  userkey: number,
  user: Pick<User, 'username' | 'email' | 'parentkey'>
) => {
  const [by, setBy] = useState<eSetBy>(userCache.get(userkey) ?? eSetBy.EMAIL);
  const { username, email, parentkey } = user || {};

  useEffect(() => {
    if (userCache.has(userkey)) {
      // Previously cached.
      setBy(userCache.get(userkey));
    } else if (
      !username ||
      0 === username.localeCompare(email, undefined, { sensitivity: 'accent' })
    ) {
      // Bracken 2 case: Email matches username.
      setBy(eSetBy.EMAIL);
    } else if (!!parentkey) {
      // Linked account: Show username instead of (parent's) email.
      setBy(eSetBy.USERNAME);
    } else {
      // Need to check external providers.
      const authorization = `Bearer ${AuthService.getAccessToken()}`;
      setBy(eSetBy.EMAIL);
      fetch(`${config.API}/api/authscheme/list/external/${userkey}`, {
        headers: { authorization }
      })
        .then((res) => res.json())
        .then((authSchemes: object[]) => {
          const by = authSchemes.length
            ? // Linked to external provider(s): Use email, since username might be external ID.
              eSetBy.EMAIL
            : // Default: Use username.
              eSetBy.USERNAME;
          userCache.set(userkey, by);
          setBy(by);
        });
    }
  }, [userkey, username, email, parentkey]);

  return {
    /** Identifying label to use, either email address or username. */
    label: by === eSetBy.EMAIL ? email : username,
    /** Icon to show, corresponding to email or username. */
    Icon: by === eSetBy.EMAIL ? EmailIcon : PersonIcon,
    /** Username to show, if different from email and not associated with external provider. */
    showUsername: by === eSetBy.EMAIL ? undefined : username
  };
};

export default useExternalUser;
